import React from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView, trackEvent } from '@fs/zion-analytics'
import { Title } from '@fs/zion-head'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useContainerWidth, Grid, Cell, InputGroup, H1 } from '@fs/zion-ui'
import ZionForm, { FormTextField, FormAutosuggest, useZionForm } from '@fs/zion-form'
import { css } from '@emotion/core'
import { getAnonSession } from '@fs/zion-session'
import { appPath } from '@fs/zion-config'
import icon from '../images/AAM.logo.svg'
import background from '../images/AAM-100.nologo_AAM.png'
import { setSessionStorageItem } from '../../../shared/helpers/sessionStorage'
import { useRegionLanguage, useCorrections } from '../hooks/hooks'
import { useDSCDispatch, ACTIONS } from '../../../state/DSCStore'
import ConditionalHeader from '../../../shared/components/ConditionalHeader/ConditionalHeader'

export default function AAMUnLandingPage() {
  const initData = { page_detail: 'All About Me - Landing' }
  usePageView(initData)
  const [t] = useTranslation()
  const splashImage = background
  const splashImageAlt = t('dlp.discovery-landing-page.aam.title', 'All About Me')
  const headerText = t('dlp.discovery-landing-page.aam.title', 'All About Me')
  const buttonColor = '#BD0057'
  const signInHref = `/auth/familysearch/login?returnUrl=${appPath}/about`
  const appName = 'All About Me'
  const title = t('landing-page.aam.page.title', 'Discover All About Me')
  const description = t(
    'dlp.discovery-landing-page.aam.whatisspecial',
    `What is special about your name and birth year?`
  )
  const atWidth = useContainerWidth()
  const DSCDispatch = useDSCDispatch()
  const isCorrections = useCorrections()
  const countryLanguageOptions = useRegionLanguage(t)

  setSessionStorageItem({ guestTree: [] })

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? `${Number.parseInt(result[1], 16)},${Number.parseInt(result[2], 16)},${Number.parseInt(result[3], 16)}`
      : null
  }

  const mainCSS = css`
    display: flex;
    flex-direction: column;
  `
  const imgCSS = css`
    width: 100%;
    max-height: 400px;
    object-fit: cover;
  `
  const iconAndTitleWrapperCSS = css`
    margin: 1vw;
    display: flex;
    color: white;
    position: absolute;
    height: 100%;
    font-family: 'museo', Verdana, Ayuthaya, 'HanaMinBFont', serif;
  `
  const bannerSectionCSS = css`
    position: relative;
  `
  const iconCSS = css`
    position: relative;
    top: 61%;
    inset-inline-start: 18%;
    height: 25%;
  `
  const headerCSS = css`
    display: ${atWidth({ sm: 'block', lg: 'block' })};
    margin-top: 30px;
  `
  const contentCSS = css`
    padding-inline-start: 6%;
    padding-inline-end: ${atWidth({ default: '10px;' })};
  `
  const buttonsCSS = css`
    display: flex;
    align-items: center;
    margin-top: 20px;
    margin-bottom: 100px;
    > * {
      margin-inline-end: 20px;
    }
  `
  const customButtonCss = css`
    margin-top: auto;
    margin-bottom: auto;
    padding: 10px;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.2s;
    color: ${buttonColor};
    text-transform: uppercase;
    border: none;
    background: none;
    :hover {
      background: rgba(${hexToRgb(buttonColor)}, 0.1) !important;
    }
    :focus {
      outline: none;
    }
  `
  const customEmphasisButtonCss = css`
    margin-top: auto;
    margin-bottom: auto;
    padding: 10px 15px;
    font-weight: 500;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    color: #571023;
    text-transform: uppercase;
    border: none;
    background: #fd4e6d;
    box-shadow:
      0px 1px 3px 0px rgb(0 0 0 / 20%),
      0px 1px 1px 0px rgb(0 0 0 / 14%),
      0px 2px 1px -1px rgb(0 0 0 / 12%);
    :hover {
      transform: scale(1.1);
      background: rgba(${hexToRgb('#fd4e6d')}, 0.7) !important;
      box-shadow:
        0px 3px 5px -1px rgb(0 0 0 / 20%),
        0px 6px 10px 0px rgb(0 0 0 / 14%),
        0px 1px 18px 0px rgb(0 0 0 / 12%);
    }
    :focus {
      outline: none;
    }
  `
  const gridCss = css`
    padding-top: 30px;
  `
  const h2Css = css`
    font-size: 1.1rem;
    font-family: 'museo', Verdana, Ayuthaya, 'HanaMinBFont', serif;
  `

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .required(t('aam-landing-validation-enter-first-name', 'Please enter your given name')),
    lastName: Yup.string()
      .trim()
      .required(t('aam-landing-validation-enter-last-name', 'Please enter your surname name')),
    languageRegion: Yup.object().required(
      t('aam-landing-validation-select-country-language', 'Please select a country and language')
    ),
    birthYear: Yup.number()
      .typeError(t('aam-landing-validation-enter-birthyear', 'Please enter your birth year'))
      .required(t('aam-landing-validation-enter-birthyear', 'Please enter your birth year'))
      .min(1901, t('aam-landing-validation-enter-birthyear-after-1900', 'Please enter a birth year after 1900'))
      .max(
        new Date().getFullYear(),
        t('aam-landing-validation-enter-valid-birthyear', 'Please enter a valid birth year')
      ),
  })

  const methods = useZionForm({
    mode: 'all',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      birthYear: '',
      languageRegion: undefined,
    },
  })

  const {
    reset,
    formState: { isValid },
  } = methods

  const dispatchToGlobal = (values) => {
    getAnonSession()
      .then(() => {
        DSCDispatch({
          type: ACTIONS.POPULATE_NONSTANDARD_USER,
          data: { nonstandardUser: { ...values } },
        })
      })
      .then(() => {
        if (window.document.URL.includes('about')) {
          window.location.reload()
        }
      })
  }

  const submitAnalytics = (countryLang) => {
    if (appName === 'All About Me' && countryLang) {
      trackEvent({
        event_name: `${appName}:GUEST:SUBMIT`,
        allAboutMe_countryLanguage: countryLang,
      })
    } else trackEvent({ event_name: appName })
  }

  const passToGlobal = (values) => {
    reset()
    submitAnalytics(values.languageRegion)
    dispatchToGlobal(values)
  }

  const suppressAutocomplete = (event) => {
    event.target.setAttribute('autocomplete', 'disabled')
  }

  return (
    <div css={mainCSS}>
      <ConditionalHeader activeExperienceId="landing" />
      <Title omitSiteName>{title}</Title>
      <div css={bannerSectionCSS}>
        <div css={iconAndTitleWrapperCSS}>
          <img src={icon} css={iconCSS} alt={t('dlp.wiflandingpage.logo', 'Where am I from logo')} />
        </div>
        {!isCorrections && <img src={splashImage} css={imgCSS} alt={splashImageAlt} />}
      </div>
      <div css={contentCSS}>
        <div css={headerCSS}>
          <H1>{headerText}</H1>
          <h2 css={h2Css}>{description}</h2>
        </div>
        <ZionForm methods={methods} onSubmit={passToGlobal}>
          <div css={gridCss}>
            <Grid>
              <Cell columns={atWidth({ xl: 3 })}>
                <InputGroup label={t('dlp.landingpage.firstname', 'Your Given Name')}>
                  <FormTextField
                    data-testid="aam-un-landing-page-first-name-text-field"
                    name="firstName"
                    placeholder={t('dlp.landingpage.firstname', 'Your Given Name')}
                  />
                </InputGroup>
                <InputGroup label={t('dlp.landingpage.lastname', 'Your Surname')}>
                  <FormTextField
                    data-testid="aam-un-landing-page-last-name-text-field"
                    name="lastName"
                    placeholder={t('dlp.landingpage.lastname', 'Your Surname')}
                  />
                </InputGroup>
              </Cell>
              <Cell columns={atWidth({ xl: 3 })}>
                <InputGroup label={t('dlp.landingpage.birthyear', 'Birth Year')}>
                  <FormTextField
                    data-testid="aam-un-landing-page-birth-year-text-field"
                    name="birthYear"
                    placeholder={t('dlp.landingpage.birthyear', 'Birth Year')}
                    type="number"
                    helpMessage=""
                  />
                </InputGroup>
                <InputGroup label={t('aam-landing-validation-select-country-language')}>
                  <FormAutosuggest
                    suggestions={countryLanguageOptions}
                    name="languageRegion"
                    inputProps={{
                      onFocus: suppressAutocomplete,
                    }}
                    placeholder={t('aam-landing-validation-select-country-language')}
                  />
                </InputGroup>
              </Cell>
            </Grid>

            <div css={buttonsCSS}>
              <button type="submit" data-testid="Guest Login" css={customEmphasisButtonCss} disabled={!isValid}>
                {t('wcf.missing-modal-continue', 'Continue')}
              </button>
              {!isCorrections && (
                <button
                  type="button"
                  data-testid="Login"
                  css={customButtonCss}
                  onClick={() => {
                    window.location = signInHref
                  }}
                >
                  {t('aam-if-account', 'Or if you have an account, sign in')}
                </button>
              )}
            </div>
          </div>
        </ZionForm>
      </div>
    </div>
  )
}
